import React, { useEffect } from "react";
import { Controller, useForm } from "react-hook-form";
import { Input as AntInput, Modal, Checkbox } from "antd";
import { IState, LocationFormValues } from "../../../types/api/location";
import { useStateCreate, useStateUpdate, useCountryList } from "../../../hooks/api/location/hooks";
import ApiSelect from "../../components/ApiSelect";
import { useMessageApi } from "../../../provider/AntdNotification";

interface StateModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedItem?: IState | null;
  qData?: any;
}

const StateModal: React.FC<StateModalProps> = ({
  isOpen,
  onClose,
  selectedItem,
  qData,
}) => {
  const messageApi = useMessageApi();
  const { control, handleSubmit, reset, setValue } = useForm<LocationFormValues>({
    defaultValues: { name: "", slug: "", countryId: undefined, status: true },
  });

  const createMutation = useStateCreate({
    qData,
    onSuccess: () => {
      messageApi.success("State created successfully");
      onClose();
    },
    onError: (err: any) =>
      messageApi.error(err?.response?.data?.message || err?.message || "Failed to create state"),
  });
  const updateMutation = useStateUpdate({
    qData,
    onSuccess: () => {
      messageApi.success("State updated successfully");
      onClose();
    },
    onError: (err: any) =>
      messageApi.error(err?.response?.data?.message || err?.message || "Failed to update state"),
  });

  const { data: countriesData, refetchWithQuery: refetchCountry } = useCountryList(
    { limit: 100, page: 1 } as any,
    { enabled: isOpen }
  );

  useEffect(() => {
    if (isOpen) {
      if (selectedItem) {
        reset({
          name: selectedItem.name ?? "",
          slug: selectedItem.slug ?? "",
          countryId: typeof selectedItem.countryId === 'string' ? selectedItem.countryId : (selectedItem.countryId?._id ?? ""),
          status: selectedItem.status ?? true,
        });
      } else {
        reset({ name: "", slug: "", countryId: undefined, status: true });
      }
    }
  }, [isOpen, selectedItem, reset]);

  const onSubmit = (data: LocationFormValues) => {
    if (selectedItem) {
      updateMutation.mutate({ id: selectedItem._id, payload: data });
    } else {
      createMutation.mutate(data);
    }
  };

  const isPending = createMutation.isPending || updateMutation.isPending;

  return (
    <Modal
      title={selectedItem ? "Edit State" : "Add State"}
      open={isOpen}
      onCancel={onClose}
      footer={null}
      destroyOnClose
    >
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4 mt-4">
        <Controller
          name="countryId"
          control={control}
          rules={{ required: "Country is required" }}
          render={({ field, fieldState }) => (
            <div>
              <label className="block text-sm font-medium mb-1">
                Country <span className="text-red-500">*</span>
              </label>
              <ApiSelect
                {...field}
                className="w-full"
                placeholder="Select Country"
                status={fieldState.error ? "error" : ""}
                options={countriesData?.data?.map((item) => ({
                  label: item.name,
                  value: item._id,
                }))}
                refetchWithQuery={refetchCountry}
                baseQuery={{ limit: 100, page: 1 }}
              />
              {fieldState.error && (
                <p className="text-red-500 text-xs mt-1">{fieldState.error.message}</p>
              )}
            </div>
          )}
        />

        <Controller
          name="name"
          control={control}
          rules={{ required: "Name is required" }}
          render={({ field, fieldState }) => (
            <div>
              <label className="block text-sm font-medium mb-1">
                Name <span className="text-red-500">*</span>
              </label>
              <AntInput
                {...field}
                placeholder="Enter state name"
                status={fieldState.error ? "error" : ""}
                onChange={(e) => {
                  field.onChange(e);
                  if (!selectedItem) {
                    setValue(
                      "slug",
                      e.target.value
                        .toLowerCase()
                        .replace(/[^a-z0-9]+/g, "-")
                        .replace(/(^-|-$)+/g, "")
                    );
                  }
                }}
              />
              {fieldState.error && (
                <p className="text-red-500 text-xs mt-1">{fieldState.error.message}</p>
              )}
            </div>
          )}
        />

        <Controller
          name="slug"
          control={control}
          rules={{ required: "Slug is required" }}
          render={({ field, fieldState }) => (
            <div>
              <label className="block text-sm font-medium mb-1">
                Slug <span className="text-red-500">*</span>
              </label>
              <AntInput {...field} placeholder="state-slug" status={fieldState.error ? "error" : ""} />
              {fieldState.error && (
                <p className="text-red-500 text-xs mt-1">{fieldState.error.message}</p>
              )}
            </div>
          )}
        />

        <Controller
          name="status"
          control={control}
          render={({ field: { value, onChange } }) => (
            <Checkbox checked={value} onChange={(e) => onChange(e.target.checked)}>
              Active Status
            </Checkbox>
          )}
        />

        <div className="flex justify-end gap-2 pt-4">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 border border-slate-200 rounded-md hover:bg-slate-50 transition-colors"
            disabled={isPending}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-hover transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
            disabled={isPending}
          >
            {isPending && (
              <span className="w-4 h-4 border-2 border-white/20 border-t-white rounded-full animate-spin" />
            )}
            {selectedItem ? "Update" : "Save"}
          </button>
        </div>
      </form>
    </Modal>
  );
};

export default StateModal;
